import { Component, OnInit, ViewEncapsulation, signal } from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { FuseConfirmDialogComponent } from "@fuse/components/confirm-dialog/confirm-dialog.component";
import { feedAnimations } from "app/animations";
import { EvaluationService } from "app/services/evaluation/evaluation.service";
import { OrganigramaService } from "app/services/organigrama/organigrama.service";
import { ProjectService } from "app/services/project/project.service";
import { UserService } from "app/services/user/user.service";
import { User } from "app/shared/models/user.interface";
import moment from "app/util/dayjs";
import { Observable, combineLatest } from "rxjs";
import { map, startWith } from "rxjs/operators";

@Component({
    selector: "app-panel-control",
    templateUrl: "./panel-control.component.html",
    styleUrls: ["./panel-control.component.scss"],
    encapsulation: ViewEncapsulation.None,
    animations: [feedAnimations],
    standalone: false
})
export class PanelControlComponent implements OnInit {
    readonly STATUS = ["PENDING", "DONE", "ALL"];

    selectedIndex: number;
    user: User;
    readonly evaluations = signal<Array<any>>([]);
    readonly periodList = signal<Array<any>>([]);
    readonly jobsList = signal<Array<any>>([]);
    readonly aspectsList = signal<Array<any>>([]);

    formControl = new UntypedFormControl();
    departamentList: Array<any>;
    filteredDepartaments: Observable<any[]>;

    selectedPeriod: string;
    selectedDepartament: string = "";
    selectedPuesto: string = "";
    selectedAspect: any;

    readonly currentPeriod = signal<any>(null);
    isBoss = false;

    evaluationsLoaded: boolean = false;
    readonly project = signal<any>(null); //Almacena la configuración del proyecto

    constructor(
        private dialog: MatDialog,
        private route: ActivatedRoute,
        private evaluationSvc: EvaluationService,
        private userSvc: UserService,
        private organigramaSvc: OrganigramaService,
        private projectSvc: ProjectService
    ) {}

    ngOnInit(): void {
        this.route.queryParams.subscribe((params) => {
            this.selectedIndex = params.targetIndex;
            this.userSvc.getDataUser().subscribe((data) => {
                this.user = data;

                // Cargar todos los periodos
                this.evaluationSvc.getEvaluationPeriods("EVALUATION").subscribe((data) => {
                    // console.log('periodList', data);
                    this.periodList.set(data);
                    // Seleccionar el periodo mas cercano a la fecha actual
                    this.selectPeriod(data);
                    // Cargar las evaluaciones de último periodo
                    this.loadEvaluations();
                });
                // Cargar los departamentos
                this.organigramaSvc.getAllDepartaments().subscribe((data) => {
                    // console.log('departamentList', data);
                    this.departamentList = data;
                    this.initDepartaments();
                });
            });
        });
        this.projectSvc.getProjectConfig().subscribe((data) => {
            this.project.set(data);
        });
    }

    initDepartaments() {
        this.filteredDepartaments = this.formControl.valueChanges.pipe(
            startWith(""),
            map((value) => this._filterDepartaments(value))
        );
    }

    loadEvaluations() {
        this.evaluations.set([]);
        let sortedEvaluations = [];
        this.evaluationsLoaded = false;
        this.loadPeriod().then(() => {
            // Si el periodo no está activo, no hay evaluaciones pendientes
            const _status = this.STATUS[this.selectedIndex];
            if (!(_status === "PENDING" && !this.currentPeriod()?.isActive)) {
                this.evaluationSvc
                    .getAllEvaluationPartial(
                        _status,
                        this.selectedPeriod,
                        this.user.id,
                        this.selectedDepartament,
                        this.selectedPuesto,
                        this.selectedAspect
                    )
                    .subscribe((data) => {
                        // BlueSpace
                        let loadedEvaluations = data;
                        if (this.projectSvc.getProjectId() == "94") {
                            loadedEvaluations = data.filter((ev) => ev.type !== "AUTOEVALUATION");
                        }
                        //Comprobamos si es boss
                        for (let elem of loadedEvaluations) {
                            if (elem.personId != this.user.id) {
                                this.isBoss = true;
                                break;
                            }
                        }

                        //Poner la primera la autoevaluacion
                        sortedEvaluations = sortedEvaluations.concat(
                            loadedEvaluations.filter((ev) => ev.personId == this.user.id)
                        );
                        sortedEvaluations = sortedEvaluations.concat(
                            loadedEvaluations.filter((ev) => ev.personId != this.user.id)
                        );

                        // Aplicar filtros
                        // if(this.selectedAspect) {
                        //         sortedEvaluations = sortedEvaluations.filter(e => {
                        //             let foo = false;
                        //             for(let aspect in this.selectedAspect) {
                        //                 const filterValue = this.selectedAspect[aspect];
                        //                 if(filterValue) {
                        //                     console.log(e, aspect, filterValue);
                        //                     const evalAspectValue = e.aspect.find(a => a.aspectId === aspect);
                        //                     if(evalAspectValue === filterValue) {
                        //                         foo = true;
                        //                     } else {
                        //                         foo = false;
                        //                     }
                        //                 }
                        //             }
                        //             return foo;
                        //         });
                        // }

                        this.evaluations.set(sortedEvaluations);
                        console.log("Evaluations", this.evaluations());
                        this.evaluationsLoaded = true;
                    });
            }
        });
    }

    loadPeriod() {
        return new Promise((resolve, reject) => {
            this.evaluationSvc.getEvaluationPeriodById(this.selectedPeriod).subscribe((data) => {
                this.currentPeriod.set(data);
                // Calcular si es periodo activo o pasado
                this.processPeriodDate();
                console.log("Current Period", this.currentPeriod());
                resolve(true);
                // Aspectos
                if (this.currentPeriod()?.isEnabledEvaluationAspect) {
                    this.evaluationSvc.getEvaluationAspects().subscribe((data) => {
                        this.aspectsList.set(data);
                        if (!this.selectedAspect) {
                            this.resetAspects();
                        }
                    });
                }
            });
        });
    }

    processPeriodDate() {
        const period = this.currentPeriod();
        if (!period) {
            return;
        }
        const _startDate = moment(period.startPeriod);
        const _endDate = moment(period.endPeriod);
        const _now = moment();

        if (_now > _endDate) {
            period.isActive = false;
            period.isLast = true;
        } else if (_now.isBetween(_startDate, _endDate) || period.isEnabledSaveEvaluationBeforePeriod) {
            period.isActive = true;
            period.isLast = false;
        } else {
            period.isActive = false;
            period.isLast = false;
        }
        this.currentPeriod.set({ ...period });
    }

    onChangeTab(evt) {
        this.selectedIndex = evt.index;
        this.loadEvaluations();
    }

    onChangePeriod(value) {
        this.selectedPeriod = value;
        if (this.currentPeriod()?.isEnabledEvaluationAspect) {
            this.resetAspects();
        }
    }

    onSelectDepartament() {
        if (this.formControl.value) {
            this.selectedDepartament = this.departamentList.find(
                (dep) => dep.departamento === this.formControl.value
            ).id;
        }
        if (!this.selectedDepartament) {
            this.jobsList.set([]);
            return;
        }
        this.organigramaSvc.getJob(this.selectedDepartament).subscribe((data) => {
            this.jobsList.set(data);
            // console.log('jobList', data);
        });
    }

    clearDepartament() {
        this.selectedDepartament = null;
        this.formControl.reset();
        this.initDepartaments();
        this.jobsList.set([]);
    }

    onChangePuesto(value) {
        this.selectedPuesto = value;
    }

    onChangeAspect(aspectId, value) {
        this.selectedAspect[aspectId] = value;
    }

    applyFilters() {
        console.log("Filtros", this.selectedDepartament, this.selectedPuesto, this.selectedAspect);
        this.loadEvaluations();
    }

    resetAspects() {
        this.selectedAspect = {};
        this.aspectsList().forEach((aspect) => {
            if (aspect.type === "SELECT") {
                this.selectedAspect[aspect.id] = "";
            }
        });
    }

    sendAllPendingsEvaluationsMail() {
        let observables = [];
        this.evaluations().forEach((e) => {
            if (e.isAutoevaluationPending) {
                observables.push(this.evaluationSvc.sendAutoevaluationMail(e.personId));
            }
        });
        //FIXME Controlar cuales fallan y dar feedback
        combineLatest(observables).subscribe(
            (data) => {
                this.showSuccessDialog();
            },
            (error) => {
                this.showErrorDialog();
            }
        );
    }

    private showSuccessDialog() {
        const dialogRef = this.dialog.open(FuseConfirmDialogComponent, {
            panelClass: ["panel-feedbalia"],
            disableClose: false,
            autoFocus: false,
            data: {
                confirmTitle: "Notificaciones por email enviadas correctamente.",
                showSuccess: true
            }
        });
    }

    private showErrorDialog() {
        const dialogRef = this.dialog.open(FuseConfirmDialogComponent, {
            panelClass: ["panel-feedbalia"],
            disableClose: false,
            autoFocus: false,
            data: {
                confirmTitle: "Se ha producido un error al enviar las notificaciones.",
                showError: true
            }
        });
    }

    private selectPeriod(periods) {
        let findCurrentPeriod = false;
        // Si la fecha actual está entre el comienzo y un final de un periodo, se selecciona como adtivo
        periods.forEach((p) => {
            const startPeriod = moment(p.startPeriod);
            const endPeriod = moment(p.endPeriod);
            const now = moment();
            if (now.isBetween(startPeriod, endPeriod, undefined, "[]")) {
                findCurrentPeriod = true;
                this.selectedPeriod = p.id;
            }
        });
        // Si no se ha encontrado periodo activo, se selecciona el úlitmo.
        if (!findCurrentPeriod) {
            this.selectedPeriod = periods[0].id;
        }
    }

    private _filterDepartaments(value: string): string[] {
        if (value) {
            const filterValue = value.toLowerCase();
            return this.departamentList.filter((option) => option.departamento.toLowerCase().includes(filterValue));
        } else {
            return this.departamentList;
        }
    }
}
